<template>
    <div class="seasonality-template-properties">
        <grid
            class="properties-list-grid"
            ref="grid"
            :columnDefs="columnDefs"
            :rowData="properties"
            @on-grid-ready="onGridReady"
        >
            <responsive-button
                :icon="['fas', 'file-excel']"
                @click="exportToExcelModalOpened = true"
                variant="primary"
            ></responsive-button>
            <b-button variant="primary" :href="goToVisualRatesUrl">Visual Rates</b-button>
            <validation-observer ref="excel_export_form" v-slot="{ meta, setFieldValue, errors }">
                <modal
                    v-model="exportToExcelModalOpened"
                    size="md"
                    :scrollable="false"
                    no-close-on-backdrop
                    no-close-on-esc
                    @shown="setData"
                >
                    <template #modal-title> Export Visual Rates </template>
                    <b-row class="w-100">
                        <b-col md="6" class="pr-md-0">
                            <validation-provider name="period.from_date" rules="required">
                                <form-datepicker
                                    v-model="values.period.from_date"
                                    label="From Date:"
                                    :hide-header="true"
                                    :display-format="userUnicodeDateFormat"
                                    :error="errors['period.from_date']"
                                    class="mb-1"
                                    @input="(val) => setFieldValue('period.from_date', val)"
                                />
                                <small class="text-danger d-block" v-if="isNegativePeriod">
                                    End date must be after start date.
                                </small>
                                <small class="text-danger d-block" v-if="isPeriodTooLong">
                                    Period must be less than 90 days.
                                </small>
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider name="period.to_date" rules="required">
                                <form-datepicker
                                    v-model="values.period.to_date"
                                    label="To Date:"
                                    :hide-header="true"
                                    :display-format="userUnicodeDateFormat"
                                    :error="errors['period.to_date']"
                                    ref="to_date"
                                    @input="(val) => setFieldValue('period.to_date', val)"
                                />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <template #modal-footer>
                        <b-button
                            variant="primary"
                            class="px-3"
                            @click="exportToExcel"
                            :disabled="!meta.valid || isNegativePeriod || isPeriodTooLong"
                            >Export</b-button
                        >
                        <b-button variant="warning" class="px-3" @click="exportToExcelModalOpened = false"
                            >Cancel</b-button
                        >
                    </template>
                </modal>
            </validation-observer>
        </grid>
    </div>
</template>

<script>
    import API from '../../../api';
    import { saveAs } from 'file-saver';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import Grid from '../../generic/grid/grid.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import ActionCellRenderer from './templates-action-cell-renderer.vue';
    import { templatesMixin } from '../mixins/templates-mixin';
    import { SEASONALITY_TEMPLATE_TAPE_RATES_EXCEL } from '../../../endpoints';
    import { mapGetters } from 'vuex';
    export default {
        name: 'SeasonalityTemplateProperties',
        components: {
            Grid,
            ResponsiveButton,
            Modal,
            FormDatepicker,
            actionCellRenderer: ActionCellRenderer,
        },
        mixins: [templatesMixin],
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            isNegativePeriod() {
                if (this.values && this.values.period) {
                    if (this.values.period.from_date && this.values.period.to_date) {
                        if (
                            this.generateDate(this.values.period.from_date).getTime() >
                            this.generateDate(this.values.period.to_date).getTime()
                        ) {
                            return true;
                        }
                    }
                }
            },
            isPeriodTooLong() {
                if (this.values && this.values.period) {
                    if (this.values.period.from_date && this.values.period.to_date) {
                        if (
                            this.generateDate(this.values.period.to_date).getTime() >
                            this.generateDate(this.values.period.from_date).getTime() + 90 * 24 * 60 * 60 * 1000
                        ) {
                            return true;
                        }
                    }
                }
            },
        },
        data() {
            return {
                exportToExcelModalOpened: false,
                values: {
                    period: {
                        from_date: null,
                        to_date: null,
                    },
                },
                gridColumnApi: null,
                goToVisualRatesUrl: '/app/visual-rates-tape/',
                columnDefs: [
                    {
                        field: 'order',
                        headerName: 'Order',
                        minWidth: 80,
                        maxWidth: 100,
                    },
                    {
                        field: 'bedrooms',
                        headerName: 'Bedrooms',
                        maxWidth: 110,
                    },
                    {
                        field: 'name',
                        headerName: 'Name',
                        suppressSizeToFit: true,
                        flex: 1,
                        minWidth: 250,
                    },
                    {
                        field: 'daily_rate',
                        headerName: 'Nightly Rate Delta',
                    },
                    {
                        field: 'weekend_rate',
                        headerName: 'Weekend Rate Delta',
                    },
                    {
                        field: 'weekly_rate',
                        headerName: 'Weekly Rate Delta',
                    },
                    {
                        field: 'monthly_rate',
                        headerName: 'Monthly Rate Delta',
                    },
                    {
                        field: 'seasonality_template',
                        headerName: 'Seasonality Template',
                        maxWidth: 320,
                    },
                    {
                        field: '',
                        minWidth: 85,
                        maxWidth: 85,
                        cellRenderer: 'actionCellRenderer',
                    },
                ],
            };
        },
        methods: {
            generateDate(dateString) {
                if (dateString) {
                    const datesArr = dateString.split('-');
                    return new Date(datesArr[0], datesArr[1] - 1, datesArr[2]);
                }
                return null;
            },
            onGridReady({ api: columnApi }) {
                columnApi.autoSizeColumns(
                    [
                        'order',
                        'bedrooms',
                        'daily_rate',
                        'weekend_rate',
                        'weekly_rate',
                        'monthly_rate',
                        'seasonality_template',
                    ],
                    false
                );
            },
            generateExcelFilename() {
                return `seasonality_${format(
                    parseISO(this.values.period.from_date),
                    this.userUnicodeDateFormat
                )}_${format(parseISO(this.values.period.to_date), this.userUnicodeDateFormat)}.xlsx`;
            },
            async exportToExcel() {
                this.exportToExcelModalOpened = false;
                try {
                    this.generatingExcelReportSuccessToast();
                    const url = SEASONALITY_TEMPLATE_TAPE_RATES_EXCEL(
                        this.values.period.from_date,
                        this.values.period.to_date
                    );
                    const response = await API.get(url, { responseType: 'blob' });
                    saveAs(response.data, this.generateExcelFilename());
                } catch (e) {
                    this.errorToast({ text: 'Something went wrong. Please try again later.' });
                }
            },
            setData() {
                this.$refs.excel_export_form.setValues({
                    period: {
                        from_date: this.values.period.from_date,
                        to_date: this.values.period.to_date,
                    },
                });
            },
        },
        mounted() {
            const from_date = new Date();
            const to_date = new Date();
            to_date.setDate(to_date.getDate() + 90);
            this.values.period.from_date = format(from_date, 'yyyy-MM-dd');
            this.values.period.to_date = format(to_date, 'yyyy-MM-dd');
            this.fetchTemplates(false);
            this.fetchPropertyTemplateSettings();
        },
    };
</script>

<style lang="scss" scoped>
    .properties-list-grid {
        height: calc(100vh - 116px) !important;
    }
</style>
